import React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { Tooltip } from 'antd';
import {
    InboxOutlined,
    AppstoreOutlined,
    ProjectOutlined,
    CalendarOutlined,
    BarChartOutlined,
    FileTextOutlined,
    FolderOpenOutlined,
    LeftOutlined,
    RightOutlined,
    UserOutlined,
} from '@ant-design/icons';

/**
 * ProjectSidebar — fixed to the viewport, flush with the top of the page.
 *
 * Three visual states:
 *   hidden    : w-0, overflow-hidden   — screens < 768px (phones)
 *   collapsed : w-14 (56px)            — icon-only rail, screens 768px–1279px
 *   expanded  : w-64 (256px)           — full labels, screens ≥ 1280px
 *
 * Parent controls open/closed via `isOpen` prop.
 * Parent controls hidden via `isHidden` prop (injected when viewport < 768px).
 */

const isDevOrQa = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('qaerp.dymaxtech.com')
);

// ── Complete navigation items for Dev & QA staging ──────────────────────────
const COMPLETE_NAV_ITEMS = [
    { key: 'mywork', label: 'My Work', icon: <UserOutlined /> },
    { key: 'inbox', label: 'Inbox', icon: <InboxOutlined /> },
    { key: 'dashboard', label: 'Projects', tooltip: 'Project Management Dashboard', icon: <ProjectOutlined /> },
    { key: 'overview', label: 'Tasks', tooltip: 'Task Management Dashboard', icon: <AppstoreOutlined /> },
    { key: 'milestones', label: 'Milestones', tooltip: 'Milestone Tracker', icon: <CalendarOutlined /> },
    { key: 'files', label: 'Files', icon: <FolderOpenOutlined /> },
    { key: 'reports', label: 'Reports & Analytics', icon: <BarChartOutlined /> },
];

// ── Production clean navigation items (released modules only) ───────────────
const CLEAN_PROD_NAV_ITEMS = [
    { key: 'mywork', label: 'My Work', icon: <UserOutlined /> },
    { key: 'inbox', label: 'Inbox', icon: <InboxOutlined /> },
    { key: 'dashboard', label: 'Projects', tooltip: 'Project Management Dashboard', icon: <ProjectOutlined /> },
    { key: 'overview', label: 'Tasks', tooltip: 'Task Management Dashboard', icon: <AppstoreOutlined /> },
    { key: 'milestones', label: 'Milestones', tooltip: 'Milestone Tracker', icon: <CalendarOutlined /> },
    { key: 'files', label: 'Files', icon: <FolderOpenOutlined /> },
    { key: 'reports', label: 'Reports & Analytics', icon: <BarChartOutlined /> },
];

export default function ProjectSidebar({
    isOpen,
    isHidden,
    onToggle,
    activeNav = 'overview',
    onNavChange,
    inboxBadgeCount = 0,
    myWorkBadgeCount = 0,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const role = currentAdmin?.role;

    const baseNavItems = isDevOrQa ? COMPLETE_NAV_ITEMS : CLEAN_PROD_NAV_ITEMS;

    // Role-based item filtering
    const filteredNavItems = baseNavItems.filter(item => {
        if (role === 'Client') {
            return ['inbox', 'dashboard', 'overview', 'files'].includes(item.key);
        }
        if (role === 'Employee') {
            return ['mywork', 'inbox', 'dashboard', 'overview', 'milestones', 'files', 'reports'].includes(item.key);
        }
        // Admin, super_admin, Manager — all available items in current environment
        return true;
    });

    // Fully hidden on mobile — render nothing, no rail, no toggle
    if (isHidden) {
        return null;
    }

    return (
        /*
         * Fixed wrapper — sits behind the sticky header (z-40 < header z-[1000]).
         * Width transitions between expanded (256px) and collapsed (56px).
         */
        <div
            className={`
                fixed top-0 left-0 bottom-0 z-40
                transition-all duration-300 ease-in-out
                ${isOpen ? 'w-64' : 'w-14'}
            `}
        >
            {/* ── Sidebar rail ── */}
            <aside
                className="
                    h-full w-full overflow-hidden
                    bg-white dark:bg-[#1f1f1f]
                    border-r border-[#E2E8F0] dark:border-[#303030]
                    flex flex-col pt-14
                "
            >
                <nav className="flex-1 px-3 pt-5 pb-4 flex flex-col gap-4 overflow-y-auto overflow-x-hidden">

                    {/* Section label — only visible when expanded */}
                    {isOpen && (
                        <div className="px-3.5 pb-2 text-[10px] font-bold text-[#424750] dark:text-slate-500 uppercase tracking-widest">
                            Project Management
                        </div>
                    )}

                    {filteredNavItems.map((item) => {
                        const active = activeNav === item.key;

                        // Badge count for this item
                        const badgeCount =
                            item.key === 'inbox' ? inboxBadgeCount :
                                item.key === 'mywork' ? myWorkBadgeCount :
                                    0;

                        const tooltipText = item.tooltip || (!isOpen ? item.label : undefined);

                        const buttonNode = (
                            <button
                                key={item.key}
                                onClick={() => onNavChange?.(item.key)}
                                className={`
                                    w-full flex items-center gap-3.5 rounded-xl
                                    font-headline font-semibold text-base text-left whitespace-nowrap
                                    transition-all duration-150 cursor-pointer
                                    ${isOpen ? 'px-3.5 py-2.5' : 'px-0 py-2.5 justify-center'}
                                    ${badgeCount > 0 && isOpen ? 'justify-between' : ''}
                                    ${active
                                        ? 'bg-[#EBF3FE] dark:bg-[#1e3a5f]/30 text-[#03488A] dark:text-[#00AAF1] shadow-sm border-l-2 border-[#03488A] dark:border-[#00AAF1]'
                                        : 'text-[#4A5568] dark:text-slate-400 hover:text-[#03488A] dark:hover:text-white hover:bg-white/60 dark:hover:bg-[#262626]'
                                    }
                                `}
                            >
                                {/* Icon */}
                                <span
                                    className={`
                                        text-xl flex-shrink-0 flex items-center justify-center
                                        ${active
                                            ? 'text-[#03488A] dark:text-[#00AAF1]'
                                            : 'text-[#7a8499] dark:text-slate-500'
                                        }
                                    `}
                                >
                                    {item.icon}
                                </span>

                                {/* Label — only when expanded */}
                                {isOpen && (
                                    <span className={`truncate leading-none flex-1 ${active ? 'font-bold' : ''}`}>
                                        {item.label}
                                    </span>
                                )}

                                {/* Badge — only when expanded and count > 0 */}
                                {isOpen && badgeCount > 0 && (
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${active
                                        ? 'bg-[#03488A] dark:bg-[#00AAF1] text-white dark:text-[#0d1117]'
                                        : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                                        }`}>
                                        {badgeCount}
                                    </span>
                                )}
                            </button>
                        );

                        if (tooltipText) {
                            return (
                                <Tooltip
                                    key={item.key}
                                    title={tooltipText}
                                    placement="right"
                                    mouseEnterDelay={0.15}
                                >
                                    {buttonNode}
                                </Tooltip>
                            );
                        }

                        return buttonNode;
                    })}
                </nav>
            </aside>

            {/* ── Toggle chevron pill on right edge ── */}
            <button
                onClick={onToggle}
                aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                title={isOpen ? 'Collapse' : 'Expand'}
                className="
                    absolute top-1/2 -translate-y-1/2 -right-3.5
                    w-7 h-9 rounded-lg z-50
                    bg-white dark:bg-[#1f1f1f]
                    border border-[#E2E8F0] dark:border-[#303030]
                    flex items-center justify-center
                    text-slate-500 dark:text-slate-400
                    hover:text-[#03488A] dark:hover:text-white
                    hover:bg-slate-50 dark:hover:border-slate-400
                    shadow-sm transition-all duration-150 active:scale-95
                "
            >
                {isOpen
                    ? <LeftOutlined style={{ fontSize: 11 }} />
                    : <RightOutlined style={{ fontSize: 11 }} />
                }
            </button>
        </div>
    );
}
